import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Type, Bold, Italic, List, Link, Image, Save } from 'lucide-react';
import MediaLibrary from '../MediaLibrary';
import { MediaFile } from '../MediaUploader';

interface TextStepProps {
  content: string;
  onChange: (content: string) => void;
  onSave?: () => void;
}

const TextStep: React.FC<TextStepProps> = ({ content, onChange, onSave }) => {
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [isProcessingPaste, setIsProcessingPaste] = useState(false);

  // Load content when component mounts or content changes
  useEffect(() => {
    const editor = document.getElementById('text-editor');
    if (editor) {
      // Only update if the content is actually different
      const currentContent = editor.innerHTML;
      if (content !== currentContent) {
        if (content) {
          // Clean up any RTL content by normalizing the text
          let cleanContent = content;
          // Remove any RTL markers or direction overrides
          cleanContent = cleanContent.replace(/dir="rtl"/gi, 'dir="ltr"');
          cleanContent = cleanContent.replace(/style="[^"]*direction:\s*rtl[^"]*"/gi, 'style="direction: ltr"');
          
          // Set the content with proper direction
          editor.innerHTML = cleanContent;
        } else {
          editor.innerHTML = '';
        }
        
        // Ensure LTR direction
        editor.style.direction = 'ltr';
        editor.style.textAlign = 'left';
        editor.style.unicodeBidi = 'normal';
      }
    }
  }, [content]);

  const handleFormat = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    const newContent = document.getElementById('text-editor')?.innerHTML || '';
    onChange(newContent);
  };

  const handleContentChange = () => {
    const editor = document.getElementById('text-editor');
    if (editor) {
      // Ensure LTR direction is maintained
      editor.style.direction = 'ltr';
      editor.style.textAlign = 'left';
      editor.style.unicodeBidi = 'normal';
      
      const newContent = editor.innerHTML || '';
      onChange(newContent);
    }
  };

  // Image compression function (same as MediaUploader)
  const compressImage = (file: File, maxWidth: number = 1920, maxHeight: number = 1080, quality: number = 0.8): Promise<File> => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Calculate new dimensions
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;

        // Draw and compress
        ctx?.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob((blob) => {
          if (blob) {
            const compressedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now()
            });
            resolve(compressedFile);
          } else {
            reject(new Error('Failed to compress image'));
          }
        }, file.type, quality);
      };

      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  };

  // Handle pasted images
  const handlePaste = async (event: ClipboardEvent) => {
    const items = event.clipboardData?.items;
    if (!items) return;

    const imageItems = Array.from(items).filter(item => item.type.startsWith('image/'));
    
    if (imageItems.length === 0) return;

    event.preventDefault();
    setIsProcessingPaste(true);

    try {
      for (const item of imageItems) {
        const file = item.getAsFile();
        if (!file) continue;

        // Compress image if it's larger than 1MB
        let processedFile = file;
        if (file.size > 1024 * 1024) {
          try {
            processedFile = await compressImage(file);
          } catch (error) {
            console.warn('Failed to compress pasted image, using original:', error);
          }
        }

        // Convert to base64
        const reader = new FileReader();
        reader.onload = (e) => {
          const dataUrl = e.target?.result as string;
          if (dataUrl) {
            const editor = document.getElementById('text-editor');
            if (editor) {
              const img = document.createElement('img');
              img.src = dataUrl;
              img.alt = 'Pasted image';
              img.className = 'max-w-full h-auto rounded-lg my-4';
              
              // Insert at cursor position or append
              const selection = window.getSelection();
              if (selection && selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);
                range.deleteContents();
                range.insertNode(img);
                range.setStartAfter(img);
                range.collapse(true);
                selection.removeAllRanges();
                selection.addRange(range);
              } else {
                editor.appendChild(img);
              }
              
              handleContentChange();
            }
          }
        };
        reader.readAsDataURL(processedFile);
      }
    } catch (error) {
      console.error('Error processing pasted image:', error);
    } finally {
      setIsProcessingPaste(false);
    }
  };

  const handleMediaSelect = (mediaFile: MediaFile) => {
    const editor = document.getElementById('text-editor');
    if (editor) {
      const img = document.createElement('img');
      img.src = mediaFile.data;
      img.alt = mediaFile.alt || mediaFile.name;
      img.className = 'max-w-full h-auto rounded-lg my-4';
      
      if (mediaFile.caption) {
        const caption = document.createElement('p');
        caption.className = 'text-sm text-gray-600 text-center italic mb-4';
        caption.textContent = mediaFile.caption;
        
        const container = document.createElement('div');
        container.className = 'my-4';
        container.appendChild(img);
        container.appendChild(caption);
        
        editor.appendChild(container);
      } else {
        editor.appendChild(img);
      }
      
      handleContentChange();
    }
    setShowMediaLibrary(false);
  };

  const formatButtons = [
    { icon: Bold, command: 'bold', tooltip: 'Bold' },
    { icon: Italic, command: 'italic', tooltip: 'Italic' },
    { icon: List, command: 'insertUnorderedList', tooltip: 'Bullet List' },
    { icon: Link, command: 'createLink', tooltip: 'Insert Link' },
  ];

  return (
    <motion.div
      className="bg-white rounded-lg border border-gray-200 overflow-hidden"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-[#667eea] to-[#764ba2] px-6 py-4 text-white">
        <div className="flex items-center space-x-2">
          <Type className="w-5 h-5" />
          <h3 className="font-semibold">Text Step</h3>
        </div>
      </div>

      {/* Toolbar */}
      <div className="border-b border-gray-200 p-4">
        <div className="flex items-center space-x-2">
          {formatButtons.map((button) => (
            <button
              key={button.command}
              onClick={() => handleFormat(button.command)}
              className="p-2 rounded hover:bg-gray-100 transition-colors"
              title={button.tooltip}
            >
              <button.icon className="w-4 h-4 text-gray-600" />
            </button>
          ))}
          <div className="w-px h-6 bg-gray-300 mx-2" />
          <button
            onClick={() => setShowMediaLibrary(true)}
            className="flex items-center space-x-2 px-3 py-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
            title="Insert Media"
          >
            <Image className="w-4 h-4" />
            <span className="text-sm font-medium">Media</span>
          </button>
          <div className="w-px h-6 bg-gray-300 mx-2" />
          <button
            onClick={onSave}
            className="flex items-center space-x-2 px-3 py-2 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
          >
            <Save className="w-4 h-4" />
            <span className="text-sm font-medium">Save</span>
          </button>
        </div>
      </div>

      {/* Editor */}
      <div className="p-6 relative">
        <div
          id="text-editor"
          contentEditable
          className="min-h-[400px] p-4 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#667eea] focus:border-transparent outline-none prose prose-sm max-w-none"
          onInput={handleContentChange}
          onBlur={() => {}}
          onFocus={() => {}}
          onPaste={handlePaste}
          style={{
            lineHeight: '1.6',
            fontSize: '14px',
            direction: 'ltr',
            textAlign: 'left',
            unicodeBidi: 'normal'
          }}
          dir="ltr"
        />
        
        {!content && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-gray-400 text-sm">Start typing your tutorial content or paste images...</p>
          </div>
        )}
        
        {/* Processing indicator for pasted images */}
        {isProcessingPaste && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none bg-white bg-opacity-75">
            <div className="flex items-center space-x-2 text-blue-600">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              <span className="text-sm font-medium">Processing pasted image...</span>
            </div>
          </div>
        )}
      </div>

      {/* Media Library Modal */}
      {showMediaLibrary && (
        <MediaLibrary
          onSelect={handleMediaSelect}
          onClose={() => setShowMediaLibrary(false)}
          isModal={true}
        />
      )}
    </motion.div>
  );
};

export default TextStep;
